import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  listenToChatRooms,
  markMessagesAsRead,
  deleteChatRoom,
} from "../firebase/firebase";
import NewChatButton from "./NewChatButton";
import styles from "./ChatList.module.css";
import { ref, onValue, get } from "firebase/database";
import { database } from "../firebase/firebase";

interface ChatRoom {
  id: string;
  otherUserId: string;
  lastMessage: {
    text: string;
    timestamp: string;
    senderId: string;
    read?: boolean;
  } | null;
  createdAt: string;
  read: boolean;
}

const ChatList: React.FC = () => {
  const { currentUser } = useAuth();
  const [chatRooms, setChatRooms] = useState<ChatRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [swipedRoomId, setSwipedRoomId] = useState<string | null>(null);
  const swipeStartX = useRef<number | null>(null);
  const swipeCurrentX = useRef<number | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser) return;

    // 실시간으로 채팅방 목록 리스닝
    const unsubscribe = listenToChatRooms(currentUser.uid, (rooms) => {
      setChatRooms(rooms);
      setLoading(false);
    });

    return () => {
      // 컴포넌트 언마운트 시 리스너 해제
      unsubscribe();
    };
  }, [currentUser]);

  const handleChatRoomClick = (chatRoomId: string) => {
    // 스와이프 상태인 경우 내비게이션하지 않음
    if (swipedRoomId) {
      setSwipedRoomId(null);
      return;
    }

    navigate(`/chat/${chatRoomId}`);
  };

  const formatTime = (timestamp: string) => {
    if (!timestamp) return "";

    const date = new Date(timestamp);
    const now = new Date();
    const isToday =
      date.getDate() === now.getDate() &&
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear();

    if (isToday) {
      return date.toLocaleTimeString("ko-KR", {
        hour: "2-digit",
        minute: "2-digit",
      });
    } else {
      return date.toLocaleDateString("ko-KR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    }
  };

  const handleSwipeStart = (
    e: React.TouchEvent | React.MouseEvent,
    roomId: string
  ) => {
    // 다른 아이템이 이미 스와이프 상태면 닫음
    if (swipedRoomId && swipedRoomId !== roomId) {
      setSwipedRoomId(null);
    }

    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    swipeStartX.current = clientX;
  };

  const handleSwipeMove = (
    e: React.TouchEvent | React.MouseEvent,
    roomId: string
  ) => {
    if (swipeStartX.current === null) return;

    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    swipeCurrentX.current = clientX;

    const swipeLength = swipeStartX.current - clientX;

    // 왼쪽으로 스와이프(음수값은 오른쪽으로 스와이프)
    if (swipeLength > 50) {
      if (swipedRoomId !== roomId) {
        setSwipedRoomId(roomId);
      }
    } else if (swipeLength < -50) {
      // 오른쪽으로 스와이프하면 액션 닫기
      if (swipedRoomId === roomId) {
        setSwipedRoomId(null);
      }
    }
  };

  const handleSwipeEnd = () => {
    swipeStartX.current = null;
    swipeCurrentX.current = null;
  };

  const handleNewMessage = (e: React.MouseEvent, roomId: string) => {
    e.stopPropagation();
    // 새로운 메시지 처리
    console.log("새 메시지:", roomId);
    setSwipedRoomId(null);
    navigate(`/chat/${roomId}`);
  };

  const handleToggleNotification = (e: React.MouseEvent, roomId: string) => {
    e.stopPropagation();
    console.log("알림 설정 변경:", roomId);
    setSwipedRoomId(null);
  };

  const handleMarkAsRead = async (e: React.MouseEvent, roomId: string) => {
    e.stopPropagation();

    // 해당 채팅방의 정보 찾기
    const room = chatRooms.find((r) => r.id === roomId);
    if (!room || !room.lastMessage || room.read) return;

    // 읽지 않은 메시지가 있고 상대방이 보낸 메시지일 경우에만 처리
    if (room.lastMessage.senderId !== currentUser?.uid) {
      // Firebase에서 해당 채팅방의 모든 메시지를 읽음 처리
      const messagesRef = ref(database, `messages/${roomId}`);
      const snapshot = await get(messagesRef);
      const unreadMessageIds: string[] = [];

      snapshot.forEach((childSnapshot) => {
        const message = childSnapshot.val();
        if (message.senderId !== currentUser?.uid && !message.read) {
          unreadMessageIds.push(childSnapshot.key as string);
        }
      });

      if (unreadMessageIds.length > 0) {
        markMessagesAsRead(roomId, unreadMessageIds);
      }
    }

    // UI 상태 업데이트
    setChatRooms((prev) =>
      prev.map((room) => (room.id === roomId ? { ...room, read: true } : room))
    );

    setSwipedRoomId(null);
  };

  const handleDeleteChat = async (e: React.MouseEvent, roomId: string) => {
    e.stopPropagation();

    if (!currentUser) return;

    if (
      window.confirm(
        "정말 이 채팅방을 삭제하시겠습니까? 모든 대화 내용이 삭제됩니다."
      )
    ) {
      try {
        // Firebase에서 채팅방 삭제
        await deleteChatRoom(roomId, currentUser.uid);
        console.log("채팅방 삭제 완료:", roomId);

        // UI에서 삭제 (실제로는 listenToChatRooms에 의해 자동으로 업데이트됨)
        setChatRooms((prev) => prev.filter((room) => room.id !== roomId));
      } catch (error) {
        console.error("채팅방 삭제 중 오류 발생:", error);
        alert("채팅방을 삭제하는 중 오류가 발생했습니다.");
      }
    }

    setSwipedRoomId(null);
  };

  if (loading) {
    return (
      <div className={styles.chatListLoading}>채팅방 목록을 불러오는 중...</div>
    );
  }

  return (
    <div className={styles.chatListContainer}>
      <div className={styles.chatListHeader}>
        <h2>채팅</h2>
      </div>

      {chatRooms.length === 0 ? (
        <div className={styles.noChats}>
          <div className={styles.emptyStateIcon}>
            <svg width="48" height="48" viewBox="0 0 24 24">
              <path
                d="M20,2H4C2.9,2 2,2.9 2,4V22L6,18H20C21.1,18 22,17.1 22,16V4C22,2.9 21.1,2 20,2M20,16H6L4,18V4H20V16Z"
                fill="#cccccc"
              />
            </svg>
          </div>
          <p>메시지가 없습니다</p>
          {/* 새로운 채팅 버튼을 리스트 마지막 항목으로 추가 */}
          <NewChatButton />
        </div>
      ) : (
        <ul className={styles.chatRoomsList}>
          {chatRooms.map((room) => (
            <li
              key={room.id}
              className={`${styles.chatRoomItem} ${
                !room.read ? styles.unread : ""
              } ${swipedRoomId === room.id ? styles.swiped : ""}`}
              onClick={() => handleChatRoomClick(room.id)}
              onTouchStart={(e) => handleSwipeStart(e, room.id)}
              onTouchMove={(e) => handleSwipeMove(e, room.id)}
              onTouchEnd={handleSwipeEnd}
              onMouseDown={(e) => handleSwipeStart(e, room.id)}
              onMouseMove={(e) => handleSwipeMove(e, room.id)}
              onMouseUp={handleSwipeEnd}
              onMouseLeave={handleSwipeEnd}
            >
              <div className={styles.chatRoomAvatar}>
                <div className={styles.avatarInitial}>
                  {room.otherUserId?.charAt(0) || "?"}
                </div>
                {!room.read && <div className={styles.notificationDot}></div>}
              </div>
              <div className={styles.chatRoomDetails}>
                <div className={styles.chatRoomHeader}>
                  <span className={styles.chatRoomName}>
                    {room.otherUserId && room.otherUserId.length > 5
                      ? `${room.otherUserId.substring(0, 5)}...`
                      : room.otherUserId || "알 수 없는 사용자"}
                  </span>
                  {room.lastMessage && (
                    <span className={styles.chatRoomTime}>
                      {formatTime(room.lastMessage.timestamp)}
                    </span>
                  )}
                </div>
                <div className={styles.chatRoomLastMessage}>
                  {room.lastMessage ? room.lastMessage.text : "새로운 채팅"}
                </div>
              </div>

              {/* 스와이프 시 보이는 액션 버튼들 */}
              <div className={styles.swipeActions}>
                <button
                  className={`${styles.swipeAction} ${styles.starAction}`}
                  onClick={(e) => handleNewMessage(e, room.id)}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
                      fill="white"
                    />
                  </svg>
                </button>
                <button
                  className={`${styles.swipeAction} ${styles.notificationAction}`}
                  onClick={(e) => handleToggleNotification(e, room.id)}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2zm-2 1H8v-6c0-2.48 1.51-4.5 4-4.5s4 2.02 4 4.5v6z"
                      fill="white"
                    />
                  </svg>
                </button>
                <button
                  className={`${styles.swipeAction} ${styles.readAction}`}
                  onClick={(e) => handleMarkAsRead(e, room.id)}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M18 7l-1.41-1.41-6.34 6.34 1.41 1.41L18 7zm4.24-1.41L11.66 16.17 7.48 12l-1.41 1.41L11.66 19l12-12-1.42-1.41zM.41 13.41L6 19l1.41-1.41L1.83 12 .41 13.41z"
                      fill="white"
                    />
                  </svg>
                </button>
                <button
                  className={`${styles.swipeAction} ${styles.deleteAction}`}
                  onClick={(e) => handleDeleteChat(e, room.id)}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"
                      fill="white"
                    />
                  </svg>
                </button>
              </div>
            </li>
          ))}

          {/* 새로운 채팅 버튼을 리스트 마지막 항목으로 추가 */}
          <li className={styles.newChatButtonItem}>
            <NewChatButton />
          </li>
        </ul>
      )}
    </div>
  );
};

export default ChatList;
